import React, { useEffect, useMemo, useState } from 'react';
import PaymentsHeader from '../features/Payments/components/PaymentsHeader';
import PaymentsTable from '../features/Payments/components/PaymentsTable';
import { usePaymentsStore } from '../features/Payments/store/paymentsStore';
import { usePackagesStore } from '../features/Packages/store/packagesStore';
import * as XLSX from 'xlsx';
import ExportModal from '../features/Payments/components/modals/ExportModal';
import { BillingPayment } from '../features/Payments/types/payment';
import {PuffLoader} from 'react-spinners';

const PaymentsContent: React.FC = () => {
    const { payments, fetchPayments } = usePaymentsStore();
    const [activeTab, setActiveTab] = useState('All');
    const [selectedService, setSelectedService] = useState('All');
    const { packages } = usePackagesStore();
    const [selectedPackage, setSelectedPackage] = useState("All");
    const { gateways } = usePaymentsStore();
    const [selectedGateway, setSelectedGateway] = useState("All");
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const loadPayments = async () => {
            if (payments.length === 0) {
                setLoading(true);
                await fetchPayments();
                setLoading(false);
            }
        };
    
        loadPayments();
    }, [payments, fetchPayments]);
    

    const filteredPayments = useMemo(() => {
        return payments.filter(pmt => {
            // Always show if both filters are 'All'
            if (activeTab === 'All' && selectedService === 'All' && selectedPackage === 'All' && selectedGateway === 'All') {
                return true;
            }

            // Filter by status if not 'All'
            const statusMatch = activeTab === 'All' || pmt.status === activeTab;

            // Filter by service if not 'All'
            const serviceMatch = selectedService === 'All' ||
                pmt.service_name?.toLowerCase() === selectedService.toLowerCase();

            // Filter by package if not 'All'
            const packageMatch = selectedPackage === 'All' ||
                pmt.product.toLowerCase() === selectedPackage.toLowerCase();

            // Filter by gateway if not 'All'
            const gatewayMatch = selectedGateway === 'All' ||
                pmt.payment_gateway?.toLowerCase() === selectedGateway.toLowerCase();

            return statusMatch && serviceMatch && packageMatch && gatewayMatch;
        });
    }, [activeTab, selectedService, selectedPackage, selectedGateway, payments]);

    const handleExport = (month: string) => {
        setIsExportModalOpen(false);
        const currentYear = new Date().getFullYear();

        const filtered = payments.filter(payment => {
            if (month === 'All Months') return true;

            const paymentDate = new Date(payment.created_at);
            return (
                paymentDate.toLocaleString('default', { month: 'long' }) === month &&
                paymentDate.getFullYear() === currentYear
            );
        });

        exportToExcel(filtered);
    };

    const exportToExcel = (payments: BillingPayment[]) => {
        // Create a new workbook
        const wb = XLSX.utils.book_new();

        // Convert filtered payments to worksheet
        const ws = XLSX.utils.json_to_sheet(payments.map(payment => ({
            'UserName': payment.username,
            'User ID': payment.userid,
            'Amount': payment.amount,
            'Currency': payment.currency,
            'Gateway': payment.payment_gateway,
            'Status': payment.status,
            'Service': payment.service_name,
            'Package': payment.product,
            'Date': payment.created_at,
            'Payer ID': payment.payerid || 'N/A'
        })));

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Payments');

        // Generate Excel file and trigger download
        XLSX.writeFile(wb, `payments_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    if (loading) {
        return <div className='w-full flex flex-col items-center justify-center h-full'><PuffLoader color="#FFFFFF" /></div>;
    }

    return (
        <div className='w-full flex flex-col items-start justify-start p-4'>
            <PaymentsHeader
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedService={selectedService}
                setSelectedService={setSelectedService}
                packages={packages}
                selectedPackage={selectedPackage}
                setSelectedPackage={setSelectedPackage}
                gateways={gateways}
                selectedGateway={selectedGateway}
                setSelectedGateway={setSelectedGateway}
                onExport={() => setIsExportModalOpen(true)}
            />
            <PaymentsTable payments={filteredPayments} />

            <ExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
            />
        </div>
    );
};

export default PaymentsContent;
